import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { useStaffAuth } from '@/contexts/StaffAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShiftModal } from '@/components/scheduling/ShiftModal';
import { PositionsDialog, StaffRolesDialog, SettingsDialog } from '@/components/scheduling/SchedulingConfigDialogs';
import { RequestsDialog, TemplatesDialog } from '@/components/scheduling/SchedulingRequestsDialogs';
import { toast } from 'sonner';
import { edDayKey, fmtEdTime, fmtEdDate, edDayStart, edAddDays, edParts, edmontonToUtc, edDateInput } from '@/lib/tz';
import {
  ChevronLeft, ChevronRight, Plus, Send, Copy, Settings as SettingsIcon, Tags, Trash2,
  AlertTriangle, Search, Users, Clock, DollarSign, CalendarDays, UserCog, Inbox, LayoutTemplate, Printer, Download,
} from 'lucide-react';

const DAY_MS = 86400000;
const pad = (n) => String(n).padStart(2, '0');

// Week start in Edmonton time (independent of the browser's own timezone).
function startOfWeek(date, weekStartsOn) {
  const start = edDayStart(date);
  const diff = (edParts(start).weekday - weekStartsOn + 7) % 7;
  return edAddDays(start, -diff);
}
const dayKey = (d) => edDayKey(d);
const fmtTime = (d, fmt) => fmtEdTime(d, fmt);

export default function Scheduling() {
  const { staff: me } = useStaffAuth();
  const [settings, setSettings] = useState(null);
  const [weekStart, setWeekStart] = useState(null);
  const [view, setView] = useState('WEEK');
  const [dayIndex, setDayIndex] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [stats, setStats] = useState(null);
  const [blocks, setBlocks] = useState({});
  const [showWages, setShowWages] = useState(false);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [shiftModal, setShiftModal] = useState(null);
  const [posDialog, setPosDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [rolesDialog, setRolesDialog] = useState(null);
  const [reqDialog, setReqDialog] = useState(false);
  const [tplDialog, setTplDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Copy-on-drag: hold ⌥ Option / Alt / Ctrl (or ⌘) to duplicate a shift instead of moving it.
  // Tracked via global key listeners so it works reliably across macOS/Windows native drag.
  const [copyHeld, setCopyHeld] = useState(false);
  const copyHeldRef = useRef(false);
  useEffect(() => {
    const setCopy = (on) => { copyHeldRef.current = on; setCopyHeld(on); };
    const isMod = (e) => e.altKey || e.metaKey || e.ctrlKey || ['Alt', 'Meta', 'Control'].includes(e.key);
    const down = (e) => { if (isMod(e)) setCopy(true); };
    const up = (e) => { if (['Alt', 'Meta', 'Control'].includes(e.key) || !(e.altKey || e.metaKey || e.ctrlKey)) setCopy(false); };
    const clear = () => setCopy(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', clear); };
  }, []);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const isAdmin = me?.role === 'ADMIN';

  // Load config once
  useEffect(() => {
    Promise.all([
      staffApi.get('/scheduling/settings').then(({ data }) => data.settings).catch(() => ({ weekStartsOn: 1, timeFormat: '12h', defaultShiftMinutes: 480, defaultBreakMinutes: 30 })),
      staffApi.get('/scheduling/positions').then(({ data }) => data.positions),
      staffApi.get('/scheduling/staff').then(({ data }) => { setShowWages(data.showWages); return data.staff; }),
    ]).then(([s, pos, st]) => {
      setSettings(s);
      setPositions(pos);
      setStaffList(st);
      setWeekStart(startOfWeek(new Date(), s.weekStartsOn));
    });
  }, []);

  const days = useMemo(() => {
    if (!weekStart) return [];
    return Array.from({ length: 7 }, (_, i) => edAddDays(weekStart, i));
  }, [weekStart]);

  const rangeIso = useMemo(() => {
    if (!weekStart) return null;
    return { from: weekStart.toISOString(), to: edAddDays(weekStart, 7).toISOString() };
  }, [weekStart]);

  const loadShifts = useCallback(() => {
    if (!rangeIso) return;
    setLoading(true);
    const params = { ...rangeIso };
    if (positionFilter !== 'all') params.positionId = positionFilter;
    if (statusFilter === 'draft') params.published = 'false';
    if (statusFilter === 'published') params.published = 'true';
    Promise.all([
      staffApi.get('/scheduling/shifts', { params }),
      staffApi.get('/scheduling/week-stats', { params: rangeIso }),
    ]).then(([sh, stt]) => {
      setShifts(sh.data.shifts);
      setBlocks(sh.data.blocks || {});
      setStats(stt.data);
    }).finally(() => setLoading(false));
  }, [rangeIso, positionFilter, statusFilter, refreshKey]);

  useEffect(() => { loadShifts(); }, [loadShifts]);

  const reloadStaff = () => staffApi.get('/scheduling/staff').then(({ data }) => { setShowWages(data.showWages); setStaffList(data.staff); });
  const reloadPositions = () => staffApi.get('/scheduling/positions').then(({ data }) => setPositions(data.positions));

  const filteredStaff = useMemo(() => {
    let list = staffList;
    if (search.trim()) list = list.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()));
    if (positionFilter !== 'all') list = list.filter((s) => s.positions.some((p) => p.id === positionFilter));
    return list;
  }, [staffList, search, positionFilter]);

  // shifts grouped by staffId (or 'OPEN') then dayKey
  const grouped = useMemo(() => {
    const map = {};
    for (const s of shifts) {
      const sk = s.staffId || 'OPEN';
      (map[sk] = map[sk] || {});
      const dk = dayKey(s.startAt);
      (map[sk][dk] = map[sk][dk] || []).push(s);
    }
    return map;
  }, [shifts]);

  const weekHoursFor = (staffId) => {
    const byDay = grouped[staffId] || {};
    let total = 0;
    for (const list of Object.values(byDay)) for (const s of list) total += s.hours.paid;
    return Math.round(total * 10) / 10;
  };

  // Unavailability / approved-time-off blocks grouped by staff → Edmonton day (for red cell markers).
  const groupedBlocks = useMemo(() => {
    const map = {};
    for (const [staffId, list] of Object.entries(blocks || {})) {
      for (const b of list) {
        const dk = dayKey(b.start);
        ((map[staffId] = map[staffId] || {})[dk] = (map[staffId][dk] || [])).push(b);
      }
    }
    return map;
  }, [blocks]);

  const hasDraft = shifts.some((s) => !s.published);

  const shiftTime = (s) => `${fmtTime(s.startAt, settings?.timeFormat)} – ${fmtTime(s.endAt, settings?.timeFormat)}`;

  // ---- Drag & drop move ----
  const onDrop = async (e, targetStaffId, day) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('shiftId');
    if (!id) return;
    const shift = shifts.find((x) => x.id === id);
    if (!shift) return;
    const sp = edParts(shift.startAt);
    const dp = edParts(day);
    const duration = new Date(shift.endAt) - new Date(shift.startAt);
    const newStart = edmontonToUtc(dp.year, dp.month, dp.day, sp.hour, sp.minute);
    const newEnd = new Date(newStart.getTime() + duration);
    const copy = copyHeldRef.current || e.altKey || e.ctrlKey || e.metaKey; // hold ⌥/Alt/Ctrl (or ⌘) to duplicate instead of move
    const payload = {
      staffId: targetStaffId,
      positionId: shift.positionId,
      startAt: newStart.toISOString(),
      endAt: newEnd.toISOString(),
      breakMinutes: shift.breakMinutes,
      breakPaid: shift.breakPaid,
      title: shift.title,
      notes: shift.notes,
      location: shift.location,
      openSlots: shift.openSlots,
    };
    try {
      if (copy) {
        await staffApi.post('/scheduling/shifts', payload);
        toast.success('Shift copied here (draft)');
      } else {
        await staffApi.patch(`/scheduling/shifts/${id}`, payload);
      }
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const publishWeek = async () => {
    try {
      const { data } = await staffApi.post('/scheduling/shifts/publish', rangeIso);
      toast.success(`Published ${data.published} shift(s)`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const copyPrevWeek = async () => {
    try {
      const prev = edAddDays(weekStart, -7);
      const { data } = await staffApi.post('/scheduling/shifts/copy-week', { fromStart: prev.toISOString(), toStart: weekStart.toISOString() });
      toast.success(`Copied ${data.created} shift(s) from last week`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const clearDraft = async () => {
    if (!window.confirm('Delete all DRAFT (unpublished) shifts in this week? Published shifts are kept.')) return;
    try {
      const { data } = await staffApi.post('/scheduling/clear-draft', rangeIso);
      toast.success(`Cleared ${data.deleted} draft shift(s)`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!weekStart || !settings) return <p className="text-muted-ink text-sm">Loading scheduler…</p>;

  const staffName = (id) => staffList.find((s) => s.id === id)?.name || 'Open Shift';

  const exportCsv = () => {
    const rows = [['Employee', 'Date', 'Day', 'Start', 'End', 'Position', 'Break (min)', 'Paid Hours', 'Status', 'Published']];
    for (const s of [...shifts].sort((a, b) => new Date(a.startAt) - new Date(b.startAt))) {
      rows.push([
        s.staffId ? staffName(s.staffId) : 'Open Shift',
        fmtEdDate(s.startAt),
        fmtEdDate(s.startAt, { weekday: 'short' }),
        fmtTime(s.startAt, settings.timeFormat), fmtTime(s.endAt, settings.timeFormat),
        s.position?.name || '', s.breakMinutes, s.hours.paid, s.status, s.published ? 'Yes' : 'Draft',
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `schedule_${weekLabel.replace(/[^0-9a-zA-Z]/g, '_')}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const printSchedule = () => {
    const head = `<tr><th>Staff</th>${days.map((d) => `<th>${fmtEdDate(d, { weekday: 'short', month: 'short', day: 'numeric' })}</th>`).join('')}</tr>`;
    const rowsHtml = filteredStaff.map((st) => {
      const cells = days.map((d) => {
        const list = (grouped[st.id] || {})[dayKey(d)] || [];
        return `<td>${list.map((s) => `<div class="sh">${fmtTime(s.startAt, settings.timeFormat)}–${fmtTime(s.endAt, settings.timeFormat)}<br/><small>${s.position?.name || ''} · ${s.hours.paid}h</small></div>`).join('') || '<span class="mut">—</span>'}</td>`;
      }).join('');
      return `<tr><td class="nm">${st.name}<br/><small>${weekHoursFor(st.id)}h/wk</small></td>${cells}</tr>`;
    }).join('');
    const html = `<html><head><title>Schedule ${weekLabel}</title><style>
      body{font-family:Arial,sans-serif;padding:20px;color:#1a1a1a}h1{font-size:18px}
      table{width:100%;border-collapse:collapse;font-size:11px}th,td{border:1px solid #ccc;padding:6px;vertical-align:top;text-align:left}
      th{background:#f5f0e8}.nm{font-weight:bold}.sh{background:#fff7ed;border-left:3px solid #E8833A;padding:3px 5px;margin-bottom:3px;border-radius:3px}.mut{color:#bbb}small{color:#666}
    </style></head><body><h1>THE TIFFIN — Weekly Schedule</h1><p>${weekLabel}</p><table>${head}${rowsHtml}</table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const visibleDays = view === 'DAY' ? [days[dayIndex]] : days;
  const weekLabel = `${fmtEdDate(days[0], { month: 'short', day: 'numeric' })} – ${fmtEdDate(days[6], { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const renderStat = (Icon, label, value, tone) => (
    <div className={`rounded-xl border px-3 py-2 flex items-center gap-2.5 min-w-[130px] ${tone === 'draft' ? 'bg-amber-50 border-amber-200' : 'bg-white border-border/40'}`} data-testid="schedule-stat">
      <Icon size={16} className={`shrink-0 ${tone === 'draft' ? 'text-amber-600' : 'text-saffron'}`} />
      <div><p className={`font-heading text-lg font-700 leading-none ${tone === 'draft' ? 'text-amber-700' : 'text-charcoal'}`}>{value}</p><p className="text-[11px] text-muted-ink">{label}</p></div>
    </div>
  );

  const renderShiftCard = (s) => {
    const crit = s.conflicts?.some((c) => c.severity === 'CRITICAL');
    const warn = s.conflicts?.length > 0;
    return (
      <button
        key={s.id}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('shiftId', s.id); e.dataTransfer.effectAllowed = 'copyMove'; }}
        onClick={() => setShiftModal({ initial: s })}
        data-testid="shift-card"
        title={s.conflicts?.map((c) => c.message).join('\n')}
        className={`w-full text-left rounded-lg px-2 py-1.5 mb-1 text-xs transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing border-l-4 ${s.published ? 'bg-white' : 'bg-amber-50'} ${crit ? 'ring-1 ring-red-400' : ''}`}
        style={{ borderLeftColor: s.position?.color || '#E8833A' }}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="font-medium text-charcoal">{shiftTime(s)}</span>
          {warn && <AlertTriangle size={12} className={crit ? 'text-red-500' : 'text-amber-500'} />}
        </div>
        {s.position && <p className="text-muted-ink truncate">{s.position.name}</p>}
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[10px] text-muted-ink">{s.hours.paid}h</span>
          {!s.published && <span className="text-[9px] uppercase font-semibold text-amber-600">Draft</span>}
          {s.published && s.updatedAfterPublish && <span className="text-[9px] uppercase font-semibold text-blue-600">Updated</span>}
          {s.published && s.status === 'CONFIRMED' && <span className="text-[9px] uppercase font-semibold text-coriander">✓</span>}
          {s.published && s.status === 'DECLINED' && <span className="text-[9px] uppercase font-semibold text-red-600">Declined</span>}
        </div>
      </button>
    );
  };

  const renderCell = (staffId, day, key) => {
    const list = (grouped[staffId] || {})[dayKey(day)] || [];
    const dayBlocks = staffId !== 'OPEN' ? ((groupedBlocks[staffId] || {})[dayKey(day)] || []) : [];
    return (
      <td
        key={key}
        className="border border-border/30 align-top p-1 min-w-[130px]"
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = (e.altKey || e.ctrlKey || e.metaKey) ? 'copy' : 'move'; }}
        onDrop={(e) => onDrop(e, staffId === 'OPEN' ? null : staffId, day)}
        data-testid="schedule-cell"
      >
        {dayBlocks.map((b, i) => {
          const allDay = (new Date(b.end) - new Date(b.start)) >= 23 * 3600 * 1000;
          return (
            <div
              key={`blk-${i}`}
              data-testid="unavailable-block"
              title={b.label || (b.critical ? 'Approved time off' : 'Marked unavailable')}
              className={`rounded-md px-2 py-1 mb-1 text-[10px] font-medium border ${b.critical ? 'bg-red-200 text-red-800 border-red-300' : 'bg-red-100 text-red-700 border-red-200'}`}
            >
              {b.critical ? 'Time off' : 'Unavailable'}{allDay ? ' · all day' : `: ${fmtTime(b.start, settings.timeFormat)}–${fmtTime(b.end, settings.timeFormat)}`}
            </div>
          );
        })}
        {list.map((s) => renderShiftCard(s))}
        <button
          onClick={() => setShiftModal({ initial: { staffId: staffId === 'OPEN' ? null : staffId, date: edDateInput(day) } })}
          data-testid="cell-add-btn"
          className="w-full text-muted-ink/40 hover:text-saffron hover:bg-bone rounded-md py-1 text-xs transition-colors"
        >
          + Add
        </button>
      </td>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-2xl font-700 text-charcoal">Scheduling</h1>
          <span data-testid="schedule-draft-indicator" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${hasDraft ? 'bg-amber-100 text-amber-700' : 'bg-coriander/10 text-coriander'}`}>
            {hasDraft ? 'Draft changes' : 'Published'}
          </span>
          <span className="text-[11px] text-muted-ink hidden md:inline" data-testid="schedule-tz-note">All times in Mountain Time (Edmonton)</span>
          {copyHeld ? (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-saffron/15 text-saffron animate-pulse" data-testid="schedule-copy-mode">＋ Copy mode — drop to duplicate</span>
          ) : (
            <span className="text-[11px] text-muted-ink hidden lg:inline" data-testid="schedule-copy-hint">· Drag to move, hold ⌥/Alt (or Ctrl) &amp; drag to copy</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" data-testid="schedule-requests-btn" onClick={() => setReqDialog(true)} className="gap-1.5"><Inbox size={14} /> Requests</Button>
          <Button variant="outline" size="sm" data-testid="schedule-templates-btn" onClick={() => setTplDialog(true)} className="gap-1.5"><LayoutTemplate size={14} /> Templates</Button>
          <Button variant="outline" size="sm" data-testid="schedule-manage-positions-btn" onClick={() => setPosDialog(true)} className="gap-1.5"><Tags size={14} /> Roles</Button>
          <Button variant="outline" size="sm" data-testid="schedule-settings-btn" onClick={() => setSettingsDialog(true)} className="gap-1.5"><SettingsIcon size={14} /> Settings</Button>
          <Button variant="outline" size="sm" data-testid="schedule-print-btn" onClick={printSchedule} className="gap-1.5"><Printer size={14} /> Print</Button>
          <Button variant="outline" size="sm" data-testid="schedule-export-btn" onClick={exportCsv} className="gap-1.5"><Download size={14} /> CSV</Button>
          <Button variant="outline" size="sm" data-testid="schedule-copy-week-btn" onClick={copyPrevWeek} className="gap-1.5"><Copy size={14} /> Copy Last Week</Button>
          <Button variant="outline" size="sm" data-testid="schedule-clear-draft-btn" onClick={clearDraft} className="gap-1.5 text-destructive"><Trash2 size={14} /> Clear Draft</Button>
          <Button size="sm" data-testid="schedule-publish-btn" onClick={publishWeek} className="bg-coriander hover:bg-coriander/90 gap-1.5"><Send size={14} /> Publish Week</Button>
          <Button size="sm" data-testid="schedule-add-shift-btn" onClick={() => setShiftModal({ initial: { staffId: filteredStaff[0]?.id || null, date: edDateInput(days[0]) } })} className="bg-saffron hover:bg-saffron-hover gap-1.5"><Plus size={14} /> Add Shift</Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="flex flex-wrap gap-2">
          {renderStat(Users, 'Employees', stats.totalEmployeesScheduled)}
          {renderStat(CalendarDays, 'Shifts', stats.totalShifts)}
          {renderStat(Clock, 'Scheduled hrs', stats.totalScheduledHours)}
          {showWages && renderStat(DollarSign, 'Total payroll', `$${stats.totalLaborCost}`)}
          {stats.totalDraftHours > 0 && renderStat(Clock, 'Draft hrs', stats.totalDraftHours, 'draft')}
          {showWages && stats.totalDraftHours > 0 && renderStat(DollarSign, 'Draft payroll', `$${stats.totalDraftLaborCost}`, 'draft')}
          {renderStat(AlertTriangle, 'Near overtime', stats.employeesNearOvertime)}
          {renderStat(UserCog, 'Open shifts', stats.openShifts)}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="week-prev-btn" onClick={() => setWeekStart(edAddDays(weekStart, -7))}><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" data-testid="week-today-btn" onClick={() => setWeekStart(startOfWeek(new Date(), settings.weekStartsOn))}>Today</Button>
          <Button variant="outline" size="icon" data-testid="week-next-btn" onClick={() => setWeekStart(edAddDays(weekStart, 7))}><ChevronRight size={16} /></Button>
          <span className="font-heading font-700 text-charcoal ml-1" data-testid="week-label">{weekLabel}</span>
          <Input type="date" data-testid="week-datepicker" className="w-40 ml-2" value={edDateInput(weekStart)} onChange={(e) => e.target.value && setWeekStart(startOfWeek(new Date(`${e.target.value}T12:00:00`), settings.weekStartsOn))} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="view-select" className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="WEEK">Week View</SelectItem>
              <SelectItem value="DAY">Day View</SelectItem>
            </SelectContent>
          </Select>
          {view === 'DAY' && (
            <Select value={String(dayIndex)} onValueChange={(v) => setDayIndex(Number(v))}>
              <SelectTrigger data-testid="day-select" className="w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                {days.map((d, i) => <SelectItem key={i} value={String(i)}>{fmtEdDate(d, { weekday: 'short', day: 'numeric' })}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-ink" />
            <Input data-testid="staff-search" placeholder="Search staff…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 w-40" />
          </div>
          <Select value={positionFilter} onValueChange={setPositionFilter}>
            <SelectTrigger data-testid="position-filter" className="w-32"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter" className="w-32"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All shifts</SelectItem>
              <SelectItem value="draft">Draft only</SelectItem>
              <SelectItem value="published">Published only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Board */}
      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <table className="w-full border-collapse" data-testid="schedule-board">
          <thead>
            <tr className="bg-bone/60">
              <th className="border border-border/30 p-2 text-left text-xs font-semibold text-muted-ink w-44 sticky left-0 bg-bone/60 z-10">Staff</th>
              {visibleDays.map((d, i) => (
                <th key={i} className="border border-border/30 p-2 text-xs font-semibold text-charcoal">
                  {fmtEdDate(d, { weekday: 'short' })}<br /><span className="text-muted-ink font-normal">{fmtEdDate(d, { month: 'short', day: 'numeric' })}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Open shifts row */}
            <tr>
              <td className="border border-border/30 p-2 sticky left-0 bg-white z-10">
                <p className="text-sm font-medium text-saffron">Open Shifts</p>
                <p className="text-[11px] text-muted-ink">Unassigned</p>
              </td>
              {visibleDays.map((d, i) => renderCell('OPEN', d, i))}
            </tr>
            {filteredStaff.map((s) => {
              const hrs = stats?.perStaffHours?.[s.id] ?? weekHoursFor(s.id);
              const overtime = settings.overtimeThreshold > 0 && hrs >= settings.overtimeThreshold;
              return (
              <tr key={s.id} data-testid="schedule-staff-row">
                <td className="border border-border/30 p-2 sticky left-0 bg-white z-10">
                  <div className="flex items-center justify-between gap-1">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">{s.name}</p>
                      <p className="text-[11px] text-muted-ink truncate">{s.positions.find((p) => p.isPrimary)?.name || s.positions[0]?.name || s.role}</p>
                    </div>
                    <button data-testid="staff-roles-open-btn" onClick={() => setRolesDialog(s)} className="text-muted-ink hover:text-saffron shrink-0" title="Roles & wage"><UserCog size={14} /></button>
                  </div>
                  <p className={`text-[11px] font-semibold mt-1 ${overtime ? 'text-red-600' : 'text-charcoal'}`} data-testid="staff-week-hours" title={overtime ? `Over the ${settings.overtimeThreshold}h weekly overtime threshold` : undefined}>
                    {hrs}h
                    {showWages && stats?.perStaffCost?.[s.id] != null && <span className={overtime ? 'text-red-600' : 'text-coriander'}> · ${stats.perStaffCost[s.id]}</span>}
                    {' / wk'}
                    {overtime && <span className="ml-1 px-1 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold" data-testid="staff-ot-badge">OT</span>}
                  </p>
                  {stats?.perStaffDraftHours?.[s.id] > 0 && (
                    <p className="text-[11px] font-medium text-amber-600 mt-0.5" data-testid="staff-draft-hours">
                      {stats.perStaffDraftHours[s.id]}h draft
                      {showWages && stats?.perStaffDraftCost?.[s.id] != null && <span> · ${stats.perStaffDraftCost[s.id]}</span>}
                    </p>
                  )}
                </td>
                {visibleDays.map((d, i) => renderCell(s.id, d, i))}
              </tr>
              );
            })}
            {filteredStaff.length === 0 && (
              <tr><td colSpan={visibleDays.length + 1} className="text-center text-muted-ink py-8 text-sm">No staff match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loading && <p className="text-xs text-muted-ink">Refreshing…</p>}

      {shiftModal && (
        <ShiftModal
          open={!!shiftModal}
          onOpenChange={(o) => !o && setShiftModal(null)}
          onSaved={refresh}
          staff={staffList}
          positions={positions}
          showWages={showWages}
          initial={shiftModal.initial}
          defaults={{ start: '09:00', durationMin: settings.defaultShiftMinutes, breakMin: settings.defaultBreakMinutes }}
        />
      )}
      <PositionsDialog open={posDialog} onOpenChange={setPosDialog} onChanged={() => { reloadPositions(); refresh(); }} />
      <SettingsDialog open={settingsDialog} onOpenChange={setSettingsDialog} isAdmin={isAdmin} onSaved={() => staffApi.get('/scheduling/settings').then(({ data }) => setSettings(data.settings))} />
      <StaffRolesDialog open={!!rolesDialog} onOpenChange={(o) => !o && setRolesDialog(null)} staffMember={rolesDialog} positions={positions} showWages={showWages} onSaved={reloadStaff} />
      <RequestsDialog open={reqDialog} onOpenChange={setReqDialog} onChanged={refresh} />
      <TemplatesDialog open={tplDialog} onOpenChange={setTplDialog} weekStart={weekStart} onApplied={refresh} />
    </div>
  );
}
